import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, RefreshCw, Image as ImageIcon, Calendar, ShieldCheck, MessageCircle, History, ExternalLink } from "lucide-react";
import { customerApi, fmtErr } from "@/customer/api";
import { requestSecureDownload } from "@/customer/downloads";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function CustomerDownloads() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    let active = true;
    customerApi.get("/customer/downloads")
      .then((r) => { if (active) setRows(r.data); })
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const reload = async () => {
    try { const r = await customerApi.get("/customer/downloads"); setRows(r.data); } catch {}
  };

  const handleRedownload = async (ppId) => {
    if (downloading[ppId]) return;
    setDownloading((s) => ({ ...s, [ppId]: true }));
    try {
      const r = await requestSecureDownload(ppId);
      toast.success(`Download started · ${r.filename}`);
      await reload();
    } catch (e) {
      toast.error(fmtErr(e) || "Could not prepare your download. Please try again.");
    } finally {
      setDownloading((s) => ({ ...s, [ppId]: false }));
    }
  };

  const totals = useMemo(() => {
    const unique = new Set(rows.map((r) => r.purchased_photo_id));
    return { count: rows.length, unique: unique.size };
  }, [rows]);

  return (
    <div data-testid="page-customer-downloads" className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-32 pb-24 space-y-8">
      <SEO title="CustomerDownloads · WelkomBiz Photography" noindex />
      <header className="space-y-3">
        <Link to="/customer" data-testid="downloads-back" className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
        </Link>
        <p className="text-[11px] tracking-luxe uppercase text-white/45">— My Downloads</p>
        <h1 className="font-display text-4xl sm:text-5xl leading-[1.05]">
          <span className="text-chrome">Every </span>
          <span className="italic text-magcyan">download</span>
          <span className="text-chrome">, on record.</span>
        </h1>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div data-testid="downloads-stat-total" className="glass chrome-border rounded-md p-4">
          <p className="text-[10px] tracking-luxe uppercase text-white/45">Total Downloads</p>
          <p className="font-display text-3xl text-chrome">{totals.count}</p>
        </div>
        <div data-testid="downloads-stat-unique" className="glass chrome-border rounded-md p-4">
          <p className="text-[10px] tracking-luxe uppercase text-white/45">Unique Photos</p>
          <p className="font-display text-3xl text-[#7fe6ff]">{totals.unique}</p>
        </div>
        <Link to="/customer/purchased" data-testid="downloads-link-purchased" className="glass chrome-border rounded-md p-4 hover:bg-white/3 transition-colors flex items-center justify-between">
          <span>
            <p className="text-[10px] tracking-luxe uppercase text-white/45">My Photos</p>
            <p className="font-display text-base text-chrome mt-1">View Purchased</p>
          </span>
          <ExternalLink className="w-4 h-4 text-[#ff2e93]" />
        </Link>
      </div>

      <div className="glass chrome-border rounded-md p-4 sm:p-5 flex items-start gap-3" data-testid="downloads-info">
        <ShieldCheck className="w-4 h-4 text-[#ff2e93] mt-0.5 shrink-0" />
        <p className="text-xs sm:text-sm text-white/65 font-body">
          Each download link is unique to your account, valid for 10 minutes only, and never stored.
          You can re-download photos you purchased <strong>without using more credits</strong>.
        </p>
      </div>

      {loading ? <p className="text-sm text-white/40">Loading…</p> :
       rows.length === 0 ? (
        <div data-testid="downloads-empty" className="glass chrome-border rounded-md p-12 text-center">
          <Download className="w-7 h-7 text-[#ff2e93] mx-auto mb-4" />
          <h2 className="font-display text-2xl text-chrome">No downloads yet</h2>
          <p className="text-sm text-white/55 mt-2">Your download history will appear here after you download a purchased photo.</p>
          <div className="mt-5 flex justify-center gap-3 flex-col sm:flex-row">
            <Link to="/customer/purchased" data-testid="downloads-empty-purchased" className="btn-primary !py-2.5 !px-5 !text-[11px]"><ImageIcon className="w-3.5 h-3.5" /> My Purchased Photos</Link>
            <Link to="/events" data-testid="downloads-empty-browse" className="btn-ghost !py-2.5 !px-5 !text-[11px]">Browse Events</Link>
          </div>
        </div>
       ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block glass chrome-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/3 text-[10px] tracking-luxe uppercase text-white/45">
                <tr>
                  <th className="text-left px-4 py-3">Photo</th>
                  <th className="text-left px-4 py-3">Event</th>
                  <th className="text-left px-4 py-3">Folder</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Total Downloads</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`downloads-row-${r.id}`} className="border-t border-white/5 hover:bg-white/3">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-md overflow-hidden chrome-border bg-black/40 shrink-0">
                          {r.thumbnail_url
                            ? <img src={r.thumbnail_url} alt={r.display_name} loading="lazy" className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center text-white/30"><ImageIcon className="w-4 h-4" /></div>}
                        </div>
                        <span className="text-white truncate max-w-[180px]">{r.display_name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/70 truncate max-w-[180px]">{r.event_title || "—"}</td>
                    <td className="px-4 py-3 text-white/55 truncate max-w-[140px]">{r.subfolder_name || "—"}</td>
                    <td className="px-4 py-3 text-white/55 whitespace-nowrap"><Calendar className="w-3 h-3 inline mr-1 text-white/40" />{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-[#7fe6ff] font-display">{r.download_count}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`downloads-redownload-${r.id}`}
                        onClick={() => handleRedownload(r.purchased_photo_id)}
                        disabled={!!downloading[r.purchased_photo_id]}
                        className="btn-ghost !py-1.5 !px-3 !text-[10px] disabled:opacity-60"
                      >
                        {downloading[r.purchased_photo_id] ? <><RefreshCw className="w-3 h-3 animate-spin" /> Preparing…</> : <><Download className="w-3 h-3" /> Download</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {rows.map((r) => (
              <div key={r.id} data-testid={`downloads-card-${r.id}`} className="glass chrome-border rounded-md p-3 flex items-start gap-3">
                <div className="w-14 h-14 rounded-md overflow-hidden chrome-border bg-black/40 shrink-0">
                  {r.thumbnail_url
                    ? <img src={r.thumbnail_url} alt={r.display_name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-white/30"><ImageIcon className="w-4 h-4" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{r.display_name || "—"}</p>
                  <p className="text-[10px] tracking-luxe uppercase text-white/45 truncate">{r.event_title}{r.subfolder_name ? ` · ${r.subfolder_name}` : ""}</p>
                  <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-0.5">{fmtDate(r.created_at)} · {r.download_count}×</p>
                  <button
                    onClick={() => handleRedownload(r.purchased_photo_id)}
                    disabled={!!downloading[r.purchased_photo_id]}
                    className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-luxe uppercase rounded-full px-3 py-1.5 bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white disabled:opacity-60"
                  >
                    {downloading[r.purchased_photo_id] ? <><RefreshCw className="w-3 h-3 animate-spin" /> Preparing…</> : <><Download className="w-3 h-3" /> Re-download</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
       )
      }

      <div className="glass chrome-border rounded-md p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <MessageCircle className="w-4 h-4 text-[#18d3ff] shrink-0" />
        <p className="text-sm text-white/65 font-body flex-1">
          Having trouble downloading? Our team will sort it out quickly.
        </p>
        <Link to="/contact" data-testid="downloads-contact" className="btn-ghost !py-2 !px-4 !text-[10px]">Contact Support</Link>
      </div>
    </div>
  );
}
